/**
 * Individual bagging add-on (The Sugar Coated Sisters)
 *
 * - Adds an "Individually bag my cookies" checkbox to cookie product forms.
 * - Cookie count comes from the product/variant name:
 *     "(1 Dozen)" = 12, "(8 MINI cookies)" = 8, "(4 MINI cookies)" = 4,
 *     "(6 BIG & 8 MINI)" = 14. Products without a count (frosting, kits) get no checkbox.
 * - When checked, the cookie line is tagged "Individually bagged: Yes", and a
 *   nested "Individual Packaging" line ($1 each) is kept in the cart under it with
 *   quantity = cookies per item x item quantity. It updates when quantities change
 *   and is removed with its parent.
 *
 * Loaded from layout/theme.liquid. Bagging product handle: individual-packaging
 */
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

const BAG_HANDLE = 'individual-packaging';
const PER_UNIT_PROP = '_bag_per_unit';
const VISIBLE_PROP = 'Individually bagged';
const EXCLUDE = /frosting|decorating|packaging/i;
const SOURCE = 'individual-bagging';

/* ---------- helpers ---------- */

const currency = window.Shopify?.currency?.active || 'USD';
const money = (cents) =>
  new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cents / 100);

const jsonCache = new Map();
function getJSON(url) {
  if (!jsonCache.has(url)) {
    jsonCache.set(
      url,
      fetch(url, { headers: { Accept: 'application/json' } }).then((r) => (r.ok ? r.json() : null)).catch(() => null)
    );
  }
  return jsonCache.get(url);
}

/** Pull a cookie count out of a product or variant name. Returns 0 if none. */
export function countFrom(text) {
  if (!text) return 0;
  const s = String(text).toLowerCase();
  let m = s.match(/(\d+)\s*big\b\D*?(\d+)\s*mini/) || s.match(/(\d+)\s*mini\b\D*?(\d+)\s*big/);
  if (m) return Number(m[1]) + Number(m[2]);
  m = s.match(/(\d+)\s*dozen/);
  if (m) return Number(m[1]) * 12;
  if (/\bdozen\b/.test(s)) return 12;
  m = s.match(/\((\d+)\s*(?:big|mini|sugar|cookie)/);
  if (m) return Number(m[1]);
  return 0;
}

function countForVariant(product, variantId) {
  if (!product || EXCLUDE.test(product.title)) return 0;
  const variant = product.variants.find((v) => String(v.id) === String(variantId));
  return countFrom(variant?.title) || countFrom(product.title);
}

let bagVariantPromise;
function getBagVariant() {
  bagVariantPromise ??= getJSON(`/products/${BAG_HANDLE}.js`).then((p) =>
    p?.variants?.[0] ? { id: p.variants[0].id, price: p.variants[0].price } : null
  );
  return bagVariantPromise;
}

/* ---------- product page checkbox ---------- */

const STYLE = `
.individual-bagging{display:flex;flex-direction:column;gap:4px;margin:0 0 16px;}
.individual-bagging[hidden]{display:none;}
.individual-bagging__label{display:flex;align-items:flex-start;gap:10px;cursor:pointer;line-height:1.4;}
.individual-bagging__checkbox{width:18px;height:18px;margin:2px 0 0;flex:none;cursor:pointer;accent-color:currentColor;}
.individual-bagging__note{font-size:0.9em;opacity:0.75;padding-left:28px;}
product-form-component.has-individual-bagging [ref="acceleratedCheckoutButtonContainer"],
product-form-component.has-individual-bagging .shopify-payment-button{display:none !important;}
.cart-items__nested-line .cart-items__remove,
.cart-items__nested-line .cart-items__quantity .quantity-minus,
.cart-items__nested-line .cart-items__quantity .quantity-plus{display:none !important;}
.cart-items__nested-line .cart-items__quantity input{pointer-events:none;}
`;
function injectStyle() {
  if (document.getElementById('individual-bagging-style')) return;
  const el = document.createElement('style');
  el.id = 'individual-bagging-style';
  el.textContent = STYLE;
  document.head.appendChild(el);
}

async function initProductForm(pfc) {
  if (pfc.dataset.baggingInit) return;
  pfc.dataset.baggingInit = 'true';

  const form = pfc.querySelector('form');
  const url = (pfc.dataset.productUrl || '').split('?')[0];
  if (!form || !url) return;

  const [product, bag] = await Promise.all([getJSON(`${url}.js`), getBagVariant()]);
  if (!product || !bag) return;
  if (!product.variants.some((v) => countForVariant(product, v.id) > 0)) return;

  injectStyle();

  const wrap = document.createElement('div');
  wrap.className = 'individual-bagging';
  wrap.innerHTML = `
    <label class="individual-bagging__label">
      <input type="checkbox" class="individual-bagging__checkbox">
      <span>Individually bag my cookies (+${money(bag.price)} per cookie)</span>
    </label>
    <div class="individual-bagging__note" aria-live="polite"></div>
    <input type="hidden" name="properties[${VISIBLE_PROP}]" value="Yes" disabled>
    <input type="hidden" name="properties[${PER_UNIT_PROP}]" value="" disabled>`;

  const buttons = form.querySelector('.product-form-buttons');
  buttons ? buttons.before(wrap) : form.append(wrap);

  const checkbox = wrap.querySelector('input[type="checkbox"]');
  const note = wrap.querySelector('.individual-bagging__note');
  const [visibleInput, perUnitInput] = wrap.querySelectorAll('input[type="hidden"]');

  const update = () => {
    const variantId = form.querySelector('input[name="id"]')?.value;
    const perUnit = countForVariant(product, variantId);
    const qty = Math.max(1, parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1);
    const on = checkbox.checked && perUnit > 0;

    wrap.hidden = perUnit === 0;
    pfc.classList.toggle('has-individual-bagging', perUnit > 0);
    visibleInput.disabled = !on;
    perUnitInput.disabled = !on;
    perUnitInput.value = String(perUnit);
    note.textContent = on ? `${perUnit * qty} cookies × ${money(bag.price)} = ${money(bag.price * perUnit * qty)}` : '';
  };

  const later = () => setTimeout(update, 0);
  checkbox.addEventListener('change', update);
  form.addEventListener('input', update);
  form.addEventListener('change', update);
  form.addEventListener('click', later);
  document.addEventListener(ThemeEvents.variantUpdate, later);
  document.addEventListener(ThemeEvents.quantitySelectorUpdate, later);
  update();
}

function scanForms(root = document) {
  root.querySelectorAll?.('product-form-component').forEach((pfc) => initProductForm(pfc));
}

/* ---------- cart sync ---------- */

async function post(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
}

async function refreshCartUI() {
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  let sections = {};
  if (sectionIds.length) {
    sections = await fetch(`${window.location.pathname}?sections=${sectionIds.join(',')}`)
      .then((r) => r.json())
      .catch(() => ({}));
  }
  const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections })
  );
}

let syncing = false;
let syncAgain = false;

async function syncBagging() {
  if (syncing) {
    syncAgain = true;
    return;
  }
  syncing = true;
  try {
    const bag = await getBagVariant();
    if (!bag) return;
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    const items = cart.items || [];
    const bagLines = items.filter((i) => i.variant_id === bag.id && i.parent_relationship?.parent_key);

    const updates = {};
    const adds = [];
    const kept = new Set();

    for (const item of items) {
      if (item.variant_id === bag.id) continue;
      const perUnit = parseInt(item.properties?.[PER_UNIT_PROP], 10);
      if (!perUnit) continue;
      const wanted = perUnit * item.quantity;
      const child = bagLines.find((b) => b.parent_relationship.parent_key === item.key);
      if (child) {
        kept.add(child.key);
        if (child.quantity !== wanted) updates[child.key] = wanted;
      } else {
        adds.push({ id: bag.id, quantity: wanted, parent_line_key: item.key });
      }
    }
    // Nested bagging lines whose parent no longer asks for bagging
    for (const b of bagLines) if (!kept.has(b.key)) updates[b.key] = 0;

    let changed = false;
    if (Object.keys(updates).length) {
      await post('/cart/update.js', { updates });
      changed = true;
    }
    if (adds.length) {
      await post('/cart/add.js', { items: adds });
      changed = true;
    }
    if (changed) await refreshCartUI();
  } catch (e) {
    console.error('[individual-bagging]', e);
  } finally {
    syncing = false;
    if (syncAgain) {
      syncAgain = false;
      syncBagging();
    }
  }
}

let syncTimer;
function scheduleSync() {
  clearTimeout(syncTimer);
  syncTimer = setTimeout(syncBagging, 150);
}

/* ---------- boot ---------- */

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  if (event.detail?.data?.source === SOURCE || event.detail?.data?.didError) return;
  scheduleSync();
});

injectStyle();
scanForms();
new MutationObserver((mutations) => {
  for (const m of mutations) for (const n of m.addedNodes) if (n.nodeType === 1) {
    if (n.matches?.('product-form-component')) initProductForm(n);
    else scanForms(n);
  }
}).observe(document.body, { childList: true, subtree: true });

scheduleSync();
